<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" :open="isOpen" class="relative z-[999]" @close="isOpen = false">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-50" />
      </TransitionChild>

      <div class="fixed inset-0 flex justify-end">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="translate-x-full"
          enter-to="translate-x-0"
          leave="duration-200 ease-in"
          leave-from="translate-x-0"
          leave-to="translate-x-full"
        >
          <DialogPanel
            class="relative flex h-full w-full max-w-[520px] flex-col bg-white shadow-xl"
          >
            <div
              class="flex shrink-0 items-center justify-end border-b border-gray-strokes px-6 py-4"
            >
              <UiButton
                variant="clear"
                variant-type="icon"
                :aria-label="$t('close')"
                @click="isOpen = false"
              >
                <UiIcon name="close" class="h-6 w-6 text-gray-elements" />
              </UiButton>
            </div>

            <div v-if="review" class="flex flex-auto flex-col overflow-y-auto [scrollbar-gutter:stable] [scrollbar-width:thin] px-6 pb-8 pt-6">
              <div class="mb-6 flex items-start gap-4">
                <div class="shrink-0">
                  <UiIcon name="user-circle" class="h-12 w-12 text-button-primary" />
                </div>
                <div class="flex flex-col gap-1">
                  <div class="break-words">{{ review.author }}</div>
                  <div v-if="review.published_at" class="text-gray-details">
                    {{ formatDate(review.published_at) }}
                  </div>
                  <UiRating :rating="Number(review.stars)" class="-ml-0.5 flex" />
                </div>
              </div>

              <p v-if="review?.currentTranslation?.title" class="mb-3 text-lg font-medium break-words">
                {{ review.currentTranslation.title }}
              </p>
              <div
                v-if="review?.currentTranslation?.description"
                class="mb-4 leading-6 break-words"
                v-html="review.currentTranslation.description"
              />

              <p
                v-if="(review as any).recommend"
                class="mb-6 text-sm italic"
              >
                {{ $t("yesIWouldRecommendToAFriend") }}
              </p>

              <div v-if="reviewMedias.length" class="relative">
                <Swiper
                  :slides-per-view="1"
                  :space-between="12"
                  class="w-full"
                  @swiper="onDrawerSwiper"
                  @slide-change="onDrawerSlideChange"
                >
                  <SwiperSlide v-for="(media, idx) in reviewMedias" :key="idx">
                    <div class="aspect-h-1 aspect-w-1">
                      <UiLazyImg
                        :src="getMediaUrl(media)"
                        :alt="review.author"
                        class="h-full w-full object-cover"
                        cover
                      />
                    </div>
                  </SwiperSlide>
                </Swiper>

                <template v-if="drawerSwiperInstance">
                  <button
                    v-if="!isDrawerFirstSlide"
                    class="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md"
                    @click="drawerSwiperInstance.slidePrev()"
                  >
                    <UiIcon name="chevron_left" class="h-5 w-5" />
                  </button>
                  <button
                    v-if="!isDrawerLastSlide"
                    class="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md"
                    @click="drawerSwiperInstance.slideNext()"
                  >
                    <UiIcon name="chevron_right" class="h-5 w-5" />
                  </button>
                </template>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  TransitionRoot,
  TransitionChild,
} from "@headlessui/vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import type { Swiper as SwiperType } from "swiper";
import { format } from "date-fns";
import { ReviewData } from "ecom-types";

const props = defineProps<{
  modelValue: boolean;
  review: ReviewData | null;
}>();

const emit = defineEmits(["update:modelValue"]);

const isOpen = computed<boolean>({
  set(val) {
    emit("update:modelValue", val);
  },
  get() {
    return props.modelValue;
  },
});

const drawerSwiperInstance = ref<SwiperType | null>(null);
const drawerActiveIndex = ref(0);

watch(
  () => props.modelValue,
  (val) => {
    if (val) {
      drawerActiveIndex.value = 0;
      nextTick(() => {
        drawerSwiperInstance.value?.slideTo(0, 0);
      });
    }
  },
);

const reviewMedias = computed(() => props.review?.medias || []);

const isDrawerFirstSlide = computed(() => drawerActiveIndex.value === 0);
const isDrawerLastSlide = computed(
  () => drawerActiveIndex.value >= reviewMedias.value.length - 1,
);

function onDrawerSwiper(swiper: SwiperType) {
  drawerSwiperInstance.value = swiper;
}

function onDrawerSlideChange(swiper: SwiperType) {
  drawerActiveIndex.value = swiper.activeIndex;
}

function getMediaUrl(media: any): string {
  return media?.file?.url || media?.url || "";
}

function formatDate(date: Date | string): string {
  return format(new Date(date), "MMMM yyyy");
}
</script>
